import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReportedPosts, updatePostStatus, setFilter, setSelectedPost } from '../redux/ReportedPostsSlice';
import { AlertTriangle, Check, X, Flag, UserCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../hooks/usePagination';
import { toast } from 'react-toastify';
import { Pagination } from '../utils/Pagination';
import { formatDate } from '../utils/Config';

// Filter Component
const PostFilter = ({ onFilterChange, currentFilter }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
      {['all', 'pending', 'retained','removed'].map(status => (
        <button
          key={status}
          onClick={() => onFilterChange(status)}
          className={`px-4 py-2 rounded-lg transition ${
            currentFilter === status 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-100 hover:bg-blue-500 hover:text-white'
          }`}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </button>
      ))}
    </div>
  );
};


// Report Card Component
const ReportCard = ({ post }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/dashboard/reported-posts/${post.id}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-lg shadow-md p-6 cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          {/* <img
            src={post?.reported_by?.profile_img}
            // alt={post.author.name}
            className="w-10 h-10 rounded-full"
          /> */}
           {post?.reported_by?.profile_img? (
                    <img
                    src={post?.reported_by?.profile_img}
                    // alt={connection.name}
                      className="w-10 h-10  rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center">
                      <UserCircle2 className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
          <div>
            <h3 className="font-medium">{`${post?.reported_by.first_name} ${post?.reported_by.last_name}`}</h3>
            <p className="text-sm text-gray-500">
              {/* {new Date(post?.created_at).toLocaleDateString()} */}
            {formatDate(post?.created_at)}
            </p>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs rounded-full ${post.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          post.status === 'retained' ? 'bg-green-100 text-green-800' : post.status === 'resolved' ? 'bg-blue-100 text-blue-800':
            'bg-red-100 text-red-800'
          }`}>
          {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
        </span>
      </div>

      <p className="text-gray-600 mb-4 line-clamp-2">{post.reason}</p>

      {/* <div className="flex items-center text-sm text-gray-500">
        <Flag size={16} className="mr-2" />
      </div> */}
    </div>
  );
};



// Main Reported Posts Page
const ReportedPosts = () => {
  const dispatch = useDispatch();
  const posts = useSelector(state => state.reportedPosts.items);
  const {
    loading,
    error,
    meta
  } = useSelector((state) => state.reportedPosts);
  
  const {
    pagination,
    handlePageChange,
    updatePaginationData,
    getPageNumbers,
    getItemRange
  } = usePagination({
    initialPage: meta.current_page || 1,
    initialPerPage: meta.per_page || 15
  });

  const [currentFilter, setCurrentFilter] = useState('all');

  useEffect(() => {
    handleFetchReportedPosts();
  }, [pagination.currentPage, currentFilter]); // Add currentFilter to dependencies

  const handleFetchReportedPosts = () => {
    const filterParams = currentFilter !== 'all' 
      ? { filter: currentFilter, filter_type: 'status' }
      : {};

    dispatch(fetchReportedPosts({
      page: pagination.currentPage,
      limit: pagination.perPage,
      ...filterParams
    }))
      .unwrap()
      .then(response => {
        if (response.meta) {
          updatePaginationData({
            current_page: response.meta.current_page,
            last_page: response.meta.last_page,
            per_page: response.meta.per_page,
            total: response.meta.total
          });
        }
      })
      .catch((error) => {
        toast.error(error.message || 'Failed to load connections');
      });
  };

  const handleFilterChange = (newFilter) => {
    setCurrentFilter(newFilter);
    handlePageChange(1);
  };

  return (
    <div className="sm:p-6">
      <div className="flex flex-col sm:flex-row items-center justify-between mb-6 ">
        <h1 className="text-2xl font-semibold">Reported Posts</h1>
        <PostFilter 
          onFilterChange={handleFilterChange}
          currentFilter={currentFilter}
        />
      </div>

      {loading ? (
      <div className="flex justify-center items-center h-[80vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
      </div>
   ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map(post => (
            <ReportCard key={post.id} post={post} />
          ))}
        </div>
      )}

      <Pagination
        currentPage={pagination.currentPage}
        lastPage={pagination.lastPage}
        total={pagination.total}
        itemRange={getItemRange()}
        pageNumbers={getPageNumbers()}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default ReportedPosts;